import React, { useState, useEffect } from "react"
import { useToast } from "~/utils/Toast"
import { apiImages } from "~/routes"
import request from "~/lib/request"
import type { Image } from "~/types/Image"

const ImageUpload = ({
  onUpload,
  currentImageUrl,
  alt: initialAlt,
  title: initialTitle,
}: {
  onUpload: (image: Image) => void
  currentImageUrl?: string
  alt?: string
  title?: string
}) => {
  const [preview, setPreview] = useState(currentImageUrl)
  const [uploading, setUploading] = useState(false)
  const [alt, setAlt] = useState(initialAlt || "")
  const [title, setTitle] = useState(initialTitle || "")
  const toast = useToast()

  useEffect(() => {
    setPreview(currentImageUrl)
    setAlt(initialAlt || "")
    setTitle(initialTitle || "")
  }, [currentImageUrl, initialAlt, initialTitle])

  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    const formData = new FormData()
    formData.append("image[file]", file)
    formData.append("image[title]", title)
    formData.append("image[alt]", alt)

    setUploading(true)
    setPreview(URL.createObjectURL(file))

    try {
      const path = apiImages.create.path({})
      const newImage = await request({ path, method: "POST", data: formData, asFormData: true })
      setPreview(newImage.url)
      onUpload(newImage)
      toast("Image uploaded", "success")
    } catch (err) {
      console.error(err)
      toast("Upload failed", "danger")
      setPreview(currentImageUrl)
    } finally {
      setUploading(false)
    }
  }

  return (
    <div>
      {preview && (
        <img
          src={preview}
          alt={alt || "preview"}
          title={title}
          className="img-fluid mb-3"
          style={{ maxHeight: 200 }}
        />
      )}

      <div className="mb-3">
        <label htmlFor="image-title" className="form-label">
          Image Title
        </label>
        <input
          type="text"
          id="image-title"
          className="form-control"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={uploading}
        />
      </div>
      <div className="mb-3">
        <label htmlFor="image-alt" className="form-label">
          Alt Text
        </label>
        <input
          type="text"
          id="image-alt"
          className="form-control"
          value={alt}
          onChange={(e) => setAlt(e.target.value)}
          disabled={uploading}
        />
      </div>

      <input type="file" onChange={handleChange} disabled={uploading} className="form-control" />
      {uploading && <p className="mt-2">Uploading...</p>}
    </div>
  )
}

export default ImageUpload
